import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Ladder } from "react-bootstrap-icons";
import "./GameBoard.css";

const LadderIcon = () => (
  <Ladder
    size={20}
    style={{
      verticalAlign: "middle",
      color: "#795548",
      fontWeight: "bold",
      stroke: "#333",
      strokeWidth: "0.5",
    }}
  />
);

const PawnToken = ({ teamColor, teamId, offsetY = 0 }) => {
  const svgWidth = 24;
  const svgHeight = 35;
  const circleRadius = 8;
  const circleCenterY = 8;

  const trianglePoints = `2, ${svgHeight - 2} 12, ${
    circleCenterY + circleRadius - 10
  } 22, ${svgHeight - 2}`;

  return (
    <div
      className="pawn-token-wrapper"
      key={teamId}
      style={{
        transform: `translateX(-50%) translateY(-${offsetY}px)`,
      }}
    >
      <svg
        className="pawn-token-svg"
        width="100%"
        height="100%"
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        preserveAspectRatio="xMidYMax meet"
      >
        <polygon
          points={trianglePoints}
          fill={teamColor}
          stroke="#333"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <circle
          cx={svgWidth / 2}
          cy={circleCenterY}
          r={circleRadius}
          fill={teamColor}
          stroke="#333"
          strokeWidth="2"
        />
      </svg>
    </div>
  );
};

const GameBoard = ({ game, selectedFilterTeam, showProgressionLines }) => {
  const boardRef = useRef(null);
  const squareRefs = useRef({});
  const [squareDimensions, setSquareDimensions] = useState({});
  const [activeSquare, setActiveSquare] = useState(null);
  const [displayedTask, setDisplayedTask] = useState(
    "Click a square to see its task or challenge.",
  );

  const squareTaskMap = useMemo(() => {
    if (!game?.board_squares) return {};
    return game.board_squares.reduce((acc, sq) => {
      acc[sq.square_index] = sq;
      return acc;
    }, {});
  }, [game?.board_squares]);

  const board_size = game?.board_size || 100;
  const cols = Math.ceil(Math.sqrt(board_size));
  const rows = Math.ceil(board_size / cols);

  const getGridPosition = useCallback(
    (squareNumber) => {
      const zeroIndexedSquare = squareNumber - 1;
      const row = Math.floor(zeroIndexedSquare / cols);
      let col = zeroIndexedSquare % cols;

      const isRTLRow = row % 2 === 0;

      if (isRTLRow) {
        col = cols - 1 - col;
      }

      const displayRow = rows - row;

      return { gridRow: displayRow, gridColumn: col + 1 };
    },
    [cols, rows],
  );

  useEffect(() => {
    if (!game) return;

    const squares = Array.from({ length: board_size }, (_, i) => i + 1);

    const updateDimensions = () => {
      if (!boardRef.current) return;
      const boardRect = boardRef.current.getBoundingClientRect();
      const newDimensions = {};

      squares.forEach((squareNum) => {
        const squareEl = squareRefs.current[squareNum];
        if (squareEl) {
          const rect = squareEl.getBoundingClientRect();

          newDimensions[squareNum] = {
            centerX: rect.left + rect.width / 2 - boardRect.left,
            centerY: rect.top + rect.height / 2 - boardRect.top,
            size: rect.width,
          };
        }
      });
      setSquareDimensions(newDimensions);
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [game, board_size]);

  const ProgressionLines = useMemo(() => {
    if (!game?.team_states || Object.keys(squareDimensions).length === 0)
      return null;

    return (
      <>
        <defs>
          {game.team_states.map((team) => (
            <marker
              key={`marker-def-${team.team_id}`}
              id={`arrow-${team.team_id}`}
              viewBox="0 0 10 10"
              refX="5"
              refY="5"
              markerWidth="10"
              markerHeight="10"
              orient="auto"
            >
              <polyline
                points="2,2 8,5 2,8"
                fill="none"
                stroke={team.team_color}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </marker>
          ))}
        </defs>

        {game.team_states.map((team) => {
          const isVisible =
            selectedFilterTeam === "All" ||
            team.team_name === selectedFilterTeam;
          if (!isVisible) return null;

          const history = team.position_history || [];
          if (history.length < 2) return null;

          const pathData = history
            .map((squareNum, idx) => {
              const coords = squareDimensions[squareNum];
              if (!coords) return null;
              return `${idx === 0 ? "M" : "L"} ${coords.centerX} ${coords.centerY}`;
            })
            .filter(Boolean)
            .join(" ");

          return (
            <g key={`progression-${team.team_id}`}>
              {/* The background glow */}
              <path
                d={pathData}
                fill="none"
                stroke={team.team_color}
                strokeWidth="3"
                strokeOpacity="0.15"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* The dashed path line */}
              <path
                d={pathData}
                fill="none"
                stroke={team.team_color}
                strokeWidth="1.2"
                strokeDasharray="4 3"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ pointerEvents: "none" }}
              />

              {/* Midpoint Arrows */}
              {history.map((squareNum, idx) => {
                if (idx === 0) return null;
                const start = squareDimensions[history[idx - 1]];
                const end = squareDimensions[squareNum];
                if (!start || !end) return null;

                const midX = (start.centerX + end.centerX) / 2;
                const midY = (start.centerY + end.centerY) / 2;

                return (
                  <line
                    key={`arrow-line-${team.team_id}-${idx}`}
                    x1={midX}
                    y1={midY}
                    x2={midX + (end.centerX - start.centerX) * 0.01}
                    y2={midY + (end.centerY - start.centerY) * 0.01}
                    stroke="transparent"
                    strokeWidth="1"
                    markerStart={`url(#arrow-${team.team_id})`}
                  />
                );
              })}

              {/* Enhanced Landing Dots */}
              {history.map((squareNum, idx) => {
                const coords = squareDimensions[squareNum];
                if (!coords) return null;
                return (
                  <circle
                    key={`dot-${team.team_id}-${idx}`}
                    cx={coords.centerX}
                    cy={coords.centerY}
                    r={5}
                    fill={team.team_color}
                    fillOpacity="0.8"
                    stroke="#333"
                    strokeWidth="0.5"
                    strokeOpacity="0.5"
                  />
                );
              })}
            </g>
          );
        })}
      </>
    );
  }, [game?.team_states, squareDimensions, selectedFilterTeam]);

  const connectionSquares = useMemo(() => {
    const snakeStartTiles = new Set();
    const ladderStartTiles = new Set();

    if (game?.board_connections) {
      game.board_connections.forEach((conn) => {
        if (conn.type === "snake") {
          snakeStartTiles.add(conn.start);
        } else if (conn.type === "ladder") {
          ladderStartTiles.add(conn.start);
        }
      });
    }
    return { snakeStartTiles, ladderStartTiles };
  }, [game?.board_connections]);

  const BoardConnections = useMemo(() => {
    if (!game || Object.keys(squareDimensions).length === 0) return null;

    const { board_connections } = game;
    const SNAKE_PALETTE = [
      "#4db6ac",
      "#9575cd",
      "#ffb74d",
      "#64b5f6",
      "#f06292",
    ];
    const LADDER_PALETTE = ["#D7CCC8", "#BCAAA4", "#E0C9A6", "#A1887F"];
    const SNAKE_OPACITY = 0.7;
    const LADDER_OPACITY = 0.7;
    const SNAKE_TONGUE_COLOR = "#ff5252";
    const LADDER_RUNG_COUNT = 7;
    const LADDER_POST_STROKE_WIDTH = 4;
    const LADDER_RUNG_STROKE_WIDTH = 3;
    const RUNG_WIDTH_RATIO = 0.25;
    const SNAKE_MAX_WIDTH = 10;
    const EYE_OFFSET_DISTANCE = 3;
    const EYE_RADIUS = 2;
    const TONGUE_LENGTH = 15;
    const TONGUE_WIGGLE_AMPLITUDE = 3;

    const generateSnakePolygonPath = (
      startX,
      startY,
      endX,
      endY,
      squareSize,
      numSegments = 100,
    ) => {
      const dx = endX - startX;
      const dy = endY - startY;
      const angle = Math.atan2(dy, dx);
      const perpAngle = angle + Math.PI / 2;
      let upperPathPoints = [];
      let lowerPathPoints = [];
      const spinePoints = [];
      for (let i = 0; i <= numSegments; i++) {
        const t = i / numSegments;
        const x_straight = startX + dx * t;
        const y_straight = startY + dy * t;
        const maxAmplitude = squareSize * 0.15;
        const graduatedAmplitude = maxAmplitude * (1 - t) ** 2;
        const wiggle_offset =
          Math.sin(t * Math.PI * 2 * 4) * graduatedAmplitude;
        const x_wavy = x_straight + wiggle_offset * Math.cos(perpAngle);
        const y_wavy = y_straight + wiggle_offset * Math.sin(perpAngle);
        spinePoints.push({ x: x_wavy, y: y_wavy });
      }
      for (let i = 0; i < spinePoints.length; i++) {
        const t = i / (spinePoints.length - 1);
        const { x: x_wavy, y: y_wavy } = spinePoints[i];
        const currentWidth = SNAKE_MAX_WIDTH * (1 - 0.8 * t);
        const halfWidth = currentWidth / 2;
        let localAngle =
          i < spinePoints.length - 1
            ? Math.atan2(
                spinePoints[i + 1].y - spinePoints[i].y,
                spinePoints[i + 1].x - spinePoints[i].x,
              )
            : angle;
        const localPerpAngle = localAngle + Math.PI / 2;
        upperPathPoints.push(
          `${x_wavy + halfWidth * Math.cos(localPerpAngle)} ${y_wavy + halfWidth * Math.sin(localPerpAngle)}`,
        );
        lowerPathPoints.unshift(
          `${x_wavy - halfWidth * Math.cos(localPerpAngle)} ${y_wavy - halfWidth * Math.sin(localPerpAngle)}`,
        );
      }
      return {
        path: `M ${upperPathPoints.join(" L ")} L ${lowerPathPoints.join(" L ")} Z`,
        spinePoints,
      };
    };

    // Note: We return a fragment containing the paths, NOT a new nested <svg>
    return (
      <>
        {board_connections.map((connection, index) => {
          const start = squareDimensions[connection.start];
          const end = squareDimensions[connection.end];
          if (!start || !end) return null;

          if (connection.type === "snake") {
            const snakeColor = SNAKE_PALETTE[index % SNAKE_PALETTE.length];
            const { path: pathData, spinePoints } = generateSnakePolygonPath(
              start.centerX,
              start.centerY,
              end.centerX,
              end.centerY,
              start.size,
            );
            const firstSpinePoint = spinePoints[0];
            const firstSegmentAngle =
              spinePoints.length > 1
                ? Math.atan2(
                    spinePoints[1].y - spinePoints[0].y,
                    spinePoints[1].x - spinePoints[0].x,
                  )
                : Math.atan2(
                    end.centerY - start.centerY,
                    end.centerX - start.centerX,
                  );
            const tongueOrientationAngle = firstSegmentAngle - Math.PI;
            const eyeX =
              firstSpinePoint.x +
              EYE_OFFSET_DISTANCE * Math.cos(firstSegmentAngle + Math.PI / 2);
            const eyeY =
              firstSpinePoint.y +
              EYE_OFFSET_DISTANCE * Math.sin(firstSegmentAngle + Math.PI / 2);

            let tonguePath = `M ${firstSpinePoint.x} ${firstSpinePoint.y}`;
            for (let i = 0; i <= 10; i++) {
              const t = i / 10;
              const x_f =
                firstSpinePoint.x +
                TONGUE_LENGTH * t * Math.cos(tongueOrientationAngle);
              const y_f =
                firstSpinePoint.y +
                TONGUE_LENGTH * t * Math.sin(tongueOrientationAngle);
              const wiggle =
                Math.sin(t * Math.PI * 4) * TONGUE_WIGGLE_AMPLITUDE;
              tonguePath += ` L ${x_f + wiggle * Math.cos(tongueOrientationAngle + Math.PI / 2)} ${y_f + wiggle * Math.sin(tongueOrientationAngle + Math.PI / 2)}`;
            }

            return (
              <g key={`snake-${index}`}>
                <circle
                  cx={firstSpinePoint.x}
                  cy={firstSpinePoint.y}
                  r={SNAKE_MAX_WIDTH / 2}
                  fill={snakeColor}
                  fillOpacity={SNAKE_OPACITY}
                />
                <path
                  d={pathData}
                  fill={snakeColor}
                  fillOpacity={SNAKE_OPACITY}
                />
                <circle cx={eyeX} cy={eyeY} r={EYE_RADIUS} fill="white" />
                <path
                  d={tonguePath}
                  fill="none"
                  stroke={SNAKE_TONGUE_COLOR}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </g>
            );
          } else {
            const ladderColor = LADDER_PALETTE[index % LADDER_PALETTE.length];
            const halfRungWidth = (start.size * RUNG_WIDTH_RATIO) / 2;
            const angle =
              Math.atan2(
                end.centerY - start.centerY,
                end.centerX - start.centerX,
              ) +
              Math.PI / 2;
            const x_off = halfRungWidth * Math.cos(angle);
            const y_off = halfRungWidth * Math.sin(angle);
            const rungs = [];
            for (let i = 1; i <= LADDER_RUNG_COUNT; i++) {
              const t = i / (LADDER_RUNG_COUNT + 1);
              const rx = start.centerX + t * (end.centerX - start.centerX);
              const ry = start.centerY + t * (end.centerY - start.centerY);
              rungs.push(
                <line
                  key={i}
                  x1={rx - x_off}
                  y1={ry - y_off}
                  x2={rx + x_off}
                  y2={ry + y_off}
                  stroke={ladderColor}
                  strokeWidth={LADDER_RUNG_STROKE_WIDTH}
                  strokeOpacity={LADDER_OPACITY}
                />,
              );
            }
            return (
              <g key={`ladder-${index}`}>
                <line
                  x1={start.centerX - x_off}
                  y1={start.centerY - y_off}
                  x2={end.centerX - x_off}
                  y2={end.centerY - y_off}
                  stroke={ladderColor}
                  strokeWidth={LADDER_POST_STROKE_WIDTH}
                  strokeOpacity={LADDER_OPACITY}
                />
                <line
                  x1={start.centerX + x_off}
                  y1={start.centerY + y_off}
                  x2={end.centerX + x_off}
                  y2={end.centerY + y_off}
                  stroke={ladderColor}
                  strokeWidth={LADDER_POST_STROKE_WIDTH}
                  strokeOpacity={LADDER_OPACITY}
                />
                {rungs}
              </g>
            );
          }
        })}
      </>
    );
  }, [game, squareDimensions]);

  const LADDER_PLACEHOLDER = "**LADDER_ICON_PLACEHOLDER**";

  const handleClick = (squareNum) => {
    if (activeSquare === squareNum) {
      setActiveSquare(null);
      setDisplayedTask("Click a square to see its task or challenge.");
      return;
    }
    setActiveSquare(squareNum);
    const taskData = squareTaskMap[squareNum];
    let task = taskData ? taskData.task_description : null;
    if (!task) {
      const connection = game?.board_connections.find(
        (c) => c.start === squareNum,
      );
      if (connection) {
        let type =
          connection.type === "snake"
            ? "Snake 🐍"
            : `Ladder ${LADDER_PLACEHOLDER}`;
        task = `${type} starts here, leading to square ${connection.end}.`;
      } else {
        task = `Square ${squareNum}.`;
      }
    }
    setDisplayedTask(task);
  };

  const renderTaskWithIcons = (task) => {
    if (typeof task !== "string") return task;
    const parts = task.split(LADDER_PLACEHOLDER);
    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < parts.length - 1 && <LadderIcon />}
      </React.Fragment>
    ));
  };

  const getTileTaskDescription = useCallback(
    (squareNum) => {
      const taskData = squareTaskMap[squareNum];
      if (taskData?.task_description) {
        let text = taskData.task_description;
        if (text === "Ladder" || text === "Snake") return null;
        return text;
      }
      return null;
    },
    [squareTaskMap],
  );

  if (!game) return <div className="game-board-loading">Loading board...</div>;

  const { team_states } = game;
  const squares = Array.from({ length: board_size }, (_, i) => i + 1);

  return (
    <div className="game-container-wrapper">
      <div
        ref={boardRef}
        className="game-board-container"
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
        }}
      >
        {squares.map((squareNum) => {
          const { gridRow, gridColumn } = getGridPosition(squareNum);
          const isWinSquare = squareNum === board_size;
          const isActive = activeSquare === squareNum;
          const isSnakeTile = connectionSquares.snakeStartTiles.has(squareNum);
          const isLadderTile =
            connectionSquares.ladderStartTiles.has(squareNum);
          const tileText = getTileTaskDescription(squareNum);
          const hasTask = !!squareTaskMap[squareNum]?.task_description;
          const squareClasses = [
            "board-square",
            isWinSquare ? "win-square" : "",
            isSnakeTile ? "snake-tile" : "",
            isLadderTile ? "ladder-tile" : "",
            hasTask ? "task-square" : "",
            isActive ? "active-square" : "",
          ]
            .filter(Boolean)
            .join(" ");
          const teamsOnSquare = team_states.filter(
            (team) => team.current_position === squareNum,
          );

          return (
            <div
              key={squareNum}
              ref={(el) => (squareRefs.current[squareNum] = el)}
              className={squareClasses}
              style={{ gridRow, gridColumn }}
              onClick={() => handleClick(squareNum)}
            >
              <span className="square-number">{squareNum}</span>
              {tileText && (
                <span className="tile-task-description" title={tileText}>
                  {tileText}
                </span>
              )}
              <div className="team-tokens">
                {teamsOnSquare.map((team, index) => (
                  <PawnToken
                    key={team.team_id}
                    teamId={team.team_id}
                    teamColor={team.team_color}
                    offsetY={index * 4}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* This single SVG container holds all overlays */}
        <svg
          className="board-connections-svg"
          width="100%"
          height="100%"
          style={{
            pointerEvents: "none",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 10,
          }}
        >
          {showProgressionLines && ProgressionLines}
          {BoardConnections}
        </svg>
      </div>
      <div className="task-display-container">
        <h3>Current Square Info:</h3>
        <p className="task-text visible">
          {renderTaskWithIcons(displayedTask)}
        </p>
      </div>
    </div>
  );
};

export default GameBoard;
